import { useState, useRef, useCallback, useEffect } from 'react';
import './CameraCapture.css';

interface CameraCaptureProps {
  onCapture: (imageBase64: string) => void;
  onClose: () => void;
}

export function CameraCapture({ onCapture, onClose }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');

  // Start camera stream
  const startCamera = useCallback(async () => {
    try {
      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setIsReady(true);
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Camera access denied. Please allow camera access in your browser settings.');
        } else if (err.name === 'NotFoundError') {
          setError('No camera found on this device.');
        } else {
          setError(`Camera error: ${err.message}`);
        }
      }
    }
  }, [facingMode]);

  // Initialize camera on mount
  useEffect(() => {
    startCamera();

    // Cleanup on unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [startCamera]);

  // Capture frame from video
  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !isReady) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current frame
    ctx.drawImage(video, 0, 0);

    // Convert to base64 (JPEG for smaller size)
    const imageBase64 = canvas.toDataURL('image/jpeg', 0.85);

    // Stop camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    // Return captured image
    onCapture(imageBase64);
  }, [isReady, onCapture]);

  // Switch between front and back camera
  const switchCamera = useCallback(() => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    onClose();
  }, [onClose]);

  if (error) {
    return (
      <div className="camera-overlay">
        <div className="camera-error">
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={handleClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="camera-overlay">
      <div className="camera-container">
        <div className="camera-header">
          <button className="btn-icon" onClick={handleClose} title="Close">
            ✕
          </button>
          <span>Take Photo of Your Work</span>
          <button className="btn-icon" onClick={switchCamera} title="Switch camera">
            🔄
          </button>
        </div>

        <div className="camera-viewfinder">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-video"
          />
          {!isReady && (
            <div className="camera-loading">
              <p>Starting camera...</p>
            </div>
          )}
        </div>

        <div className="camera-controls">
          <button
            className="capture-button"
            onClick={captureImage}
            disabled={!isReady}
            title="Capture"
          >
            <div className="capture-button-inner" />
          </button>
        </div>

        {/* Hidden canvas for capturing frame */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
}
